// src/components/NavBar.jsx
import { useState, useEffect, useRef } from "react";
import { useLocation, NavLink } from "react-router-dom";
import { HiMenu, HiX } from "react-icons/hi";
import { FaPhoneAlt, FaHome, FaInfoCircle, FaBriefcase, FaFileContract, FaArrowRight } from "react-icons/fa";
import { FaXTwitter, FaLinkedin, FaFacebook, FaInstagram, FaMedium } from "react-icons/fa6";
// import { MdWork } from "react-icons/md";
import Logo from "./Logo";
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from "@mui/material/CircularProgress";

export default function NavBar() {
    const [navOpen, setNavOpen] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [isPremium, setIsPremium] = useState(false);
    const [onHero, setOnHero] = useState(false); // true = still inside hero
    const [loading, setLoading] = useState(false);
    const [bg, setBg] = useState("bg-gradient-to-r from-blue-600 to-blue-400");

    const location = useLocation();
    const navRef = useRef(null);

    const scrollToTop = () => window.scrollTo(0, 0);

    // --- Robust hero-bottom detection (uses bounding rect)
    useEffect(() => {
        let rafId = null;
        let pollId = null;
        let attempts = 0;

        const getNavHeight = () => {
            return navRef.current ? navRef.current.offsetHeight : 0;
        };

        const updateOnHero = () => {
            const hero = document.getElementById("hero");
            // if not on home or hero missing, treat as not on hero
            if (location.pathname !== "/" || !hero) {
                setOnHero(false);
                return;
            }
            const rect = hero.getBoundingClientRect();
            const navHeight = getNavHeight();
            // stillInHero === true while hero bottom is below nav height (i.e., not scrolled past)
            const stillInHero = rect.bottom > navHeight + 2; // +2px tolerance
            setOnHero(stillInHero);
        };

        const onScroll = () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(updateOnHero);
        };

        // Initial update — but hero might mount slightly after NavBar runs,
        // so poll a few times until it's found (or stop after attempts).
        updateOnHero();
        if (!document.getElementById("hero") && location.pathname === "/") {
            attempts = 0;
            pollId = setInterval(() => {
                attempts++;
                if (document.getElementById("hero")) {
                    updateOnHero();
                    clearInterval(pollId);
                    pollId = null;
                } else if (attempts > 15) {
                    clearInterval(pollId);
                    pollId = null;
                }
            }, 100);
        }

        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);

        // If the mobile menu toggles height of nav, re-check
        // (navOpen in deps ensures re-run when menu opens/closes)
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
            if (pollId) clearInterval(pollId);
        };
    }, [location.pathname, navOpen]);

    // --- set bg according to onHero and route
    useEffect(() => {
        if (location.pathname !== "/") {
            setBg("bg-white");
            return;
        }
        setBg(onHero ? "bg-gradient-to-r from-blue-600 to-blue-400" : "bg-white");
    }, [onHero, location.pathname]);

    // localStorage for premium state (unchanged)
    useEffect(() => {
        const saved = localStorage.getItem("fixerfind_isPremium");
        if (saved === "true") setIsPremium(true);
    }, []);
    useEffect(() => {
        localStorage.setItem("fixerfind_isPremium", isPremium ? "true" : "false");
    }, [isPremium]);

    const links = [
        { to: "/", label: "Home", icon: <FaHome className="mr-2" /> },
        { to: "/about", label: "About", icon: <FaInfoCircle className="mr-2" /> },
        // { to: "/services", label: "Services", icon: className="mr-2" /> },
        { to: "/portfolio", label: "Portfolio", icon: <FaBriefcase className="mr-2" /> },
        // { to: "/blog", label: "Blog", icon: <FaBlog className="mr-2" /> },
        // { to: "/careers", label: "Careers", icon: <FaUserTie className="mr-2" /> },
        // { to: "/investors", label: "Investors", icon: <MdWork className="mr-2" /> },
        { to: "/legal", label: "Legals", icon: <FaFileContract className="mr-2" /> },
    ];

    const [open, setOpen] = useState(false);
    const [updateMessage, setUpdateMessage] = useState('');

    const handleClose = () => {
        setOpen(false);
    };

    const handleSubmitContact = async (e) => {
        e.preventDefault();

        const formData = {
            name: e.target.name.value.trim(),
            email: e.target.email.value.trim(),
            phone: e.target.phone.value.trim(),
            message: e.target.message.value.trim(),
        };

        // basic validation
        if (!formData.name || !formData.email || !formData.phone || !formData.message) {
            setOpen(true);
            setUpdateMessage("Please fill out all fields before submitting.");
            return;
        }

        try {
            setLoading(true);

            const res = await fetch("https://fixerfind-web2-backend.vercel.app/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });

            const data = await res.json();

            if (res.ok) {
                e.target.reset();
            } else {
                setUpdateMessage(data.error || "Something went wrong.");
            }
        } catch (err) {
            setUpdateMessage("Something went wrong. Please try again.");
        } finally {
            setOpen(true);
            setLoading(false);
            setUpdateMessage("Graciously recieved! - We'll reply you via email in a second.");
            e.target.reset();
        }
    };

    const handleDownload = () => {
        const ua = navigator.userAgent || navigator.vendor || window.opera;
        if (/android/i.test(ua)) {
            window.open("https://play.google.com/store/apps/details?id=com.fixerfind.fixerfind&hl=en");
        } else if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
            window.open("https://apps.apple.com/app/fixerfind-hire-offer-jobs/id6504881996");
        } else if (/Macintosh|Mac OS X/.test(ua)) {
            window.open("https://apps.apple.com/us/app/fixerfind-hire-offer-jobs/id6504881996");
        } else {
            window.open("https://play.google.com/store/apps/details?id=com.fixerfind.fixerfind&hl=en");
        }
    };

    return (
        <>
            <nav ref={navRef} className={`fixed top-0 left-0 w-full transition-colors duration-300 ${bg} shadow-md z-50`}>
                <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <NavLink to="/" className="flex items-center" onClick={scrollToTop}>
                            <Logo textColor={onHero ? "#fff" : "#1e90ff"} />
                        </NavLink>
                    </div>

                    {/* Desktop Menu */}
                    <div className="hidden md:flex items-center space-x-6">
                        {links.map((link, i) => (
                            <NavLink
                                key={i}
                                to={link.to}
                                onClick={scrollToTop}
                                className={({ isActive }) =>
                                    onHero
                                        ? `relative transition-colors duration-200 ${isActive ? "font-bold text-white after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-full after:h-[2px] after:bg-white" : "text-white hover:shadow-lg hover:text-yellow-400"}`
                                        : `relative transition-colors duration-200 ${isActive ? "font-bold text-blue-600 after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-full after:h-[2px] after:bg-blue-600" : "text-gray-700 hover:text-blue-600"}`
                                }
                            >
                                {link.label}
                            </NavLink>
                        ))}

                        <button
                            onClick={handleDownload}
                            className={`ml-2 px-4 py-2 rounded-lg font-semibold transition ${onHero ? "bg-white text-blue-600 hover:bg-gray-100" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                        >
                            Download Today!
                        </button>
                        <NavLink to="/premium" onClick={scrollToTop} className={`ml-2 px-5 py-2 rounded-lg font-semibold transition-all duration-300 backdrop-blur-md ${onHero
                            ? "bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 text-black border border-white/30 hover:bg-white/20 shadow-[0_0_20px_rgba(255,255,255,0.2)]"
                            : "bg-gray-900 text-white border border-gray-700 hover:border-indigo-500 shadow-[0_0_15px_rgba(99,102,241,0.4)]"
                            }`}>
                            Use Premium
                        </NavLink>
                    </div>

                    {/* Mobile Buttons */}
                    <div className="flex items-center gap-3 md:hidden">
                        {/* <button
                            onClick={() => { setFormOpen(true); setNavOpen(false); }}
                            aria-label="Open contact form"
                            className={`p-2 rounded-md ${onHero ? "text-white" : "text-gray-700"}`}
                        >
                            <FaPhoneAlt size={18} />
                        </button> */}

                        <button
                            className={`p-2 rounded-md ${onHero ? "text-white" : "text-gray-700"} focus:outline-none`}
                            onClick={() => setNavOpen(!navOpen)}
                            aria-label="Toggle menu"
                        >
                            {navOpen ? <HiX size={26} /> : <HiMenu size={26} />}
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile Nav Sidebar */}
            <div className={`fixed top-0 right-0 h-full w-full bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-40 ${navOpen ? "translate-x-0" : "translate-x-full"}`} aria-hidden={!navOpen}>
                <div className="flex items-center justify-between p-4 border-b">
                    <NavLink to="/" onClick={() => setNavOpen(false)} className="flex items-center">
                        <Logo textColor={onHero ? "#fff" : "#1e90ff"} />
                    </NavLink>
                    <button onClick={() => setNavOpen(false)} aria-label="Close menu"><HiX size={24} /></button>
                </div>

                <div className="p-6">
                    <nav className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {links.map((link, i) => (
                            <NavLink
                                key={i}
                                to={link.to}
                                onClick={() => {
                                    setNavOpen(false);
                                    scrollToTop();
                                }}
                                className={({ isActive }) =>
                                    `flex items-center justify-between py-3 px-4 rounded-lg border transition ${isActive
                                        ? "bg-blue-50 border-blue-600 text-blue-600 font-semibold"
                                        : "border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-blue-600"
                                    }`
                                }
                            >
                                <span className="flex items-center gap-2">
                                    {link.icon}
                                    {link.label}
                                </span>
                                <span className="flex items-center gap-2">
                                    <FaArrowRight className="mr-2" />
                                </span>
                            </NavLink>
                        ))}
                    </nav>

                    <div className="mt-6 border-t pt-4">
                        {/* Contact Button */}
                        {/* <button onClick={() => {
                            setFormOpen(true);
                            setNavOpen(false);
                        }}
                            className="mt-4 w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition"
                        >
                            Contact Us
                        </button> */}

                        <NavLink to="/premium"
                            onClick={() => { setNavOpen(false); scrollToTop(); }}
                            className="mt-4 w-full flex items-center justify-center font-semibold bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 text-black border border-white/30 hover:bg-white/20 shadow-[0_0_15px_rgba(99,102,241,0.4)] py-2 rounded-lg hover:bg-blue-700 transition"
                        >
                            Use Premium
                        </NavLink>

                        {/* Follow Us Section */}
                        <div className="flex flex-col items-center mb-4 mt-6">
                            <p className="text-gray-600 text-sm mb-2 font-medium">Follow Us:</p>
                            <div className="flex space-x-4 text-xl">
                                <a
                                    href="https://www.x.com/fixerfind"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-gray-600 hover:text-blue-600 transition"
                                >
                                    <FaXTwitter />
                                </a>
                                <a
                                    href="https://linkedin.com/company/fixerfind"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-gray-600 hover:text-blue-600 transition"
                                >
                                    <FaLinkedin />
                                </a>
                                <a
                                    href="https://www.facebook.com/profile.php?id=100093456613751"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-gray-600 hover:text-blue-600 transition"
                                >
                                    <FaFacebook />
                                </a>
                                <a
                                    href="https://instagram.com/fixerfind"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-gray-600 hover:text-blue-600 transition"
                                >
                                    <FaInstagram />
                                </a>
                                <a
                                    href="https://medium.com/@fixerfind"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-gray-600 hover:text-blue-600 transition"
                                >
                                    <FaMedium />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Overlays, slide-in contact form, etc. (unchanged) */}
            {navOpen && <div className="fixed inset-0 bg-black bg-opacity-30 z-30" onClick={() => setNavOpen(false)} aria-hidden="true" />}
            <div className={`fixed top-0 right-0 h-full w-full bg-white shadow-2xl transform transition-transform duration-300 z-50 ${formOpen ? "translate-x-0" : "translate-x-full"}`} aria-hidden={!formOpen}>
                <div className="flex items-center justify-between p-4 border-b">
                    <div className="flex items-center gap-3">
                        <FaPhoneAlt className="text-blue-600" />
                        <div>
                            <div className="font-semibold">Contact FixerFind</div>
                            <div className="text-xs text-gray-500">We’ll get back to you within 24 hours</div>
                        </div>
                    </div>
                    <button onClick={() => setFormOpen(false)} aria-label="Close contact form"><HiX size={22} /></button>
                </div>

                <div className="p-6 overflow-y-auto h-[calc(100%-72px)]">
                    <form onSubmit={handleSubmitContact} className="space-y-4">
                        <div>
                            <label className="block text-sm text-gray-700 mb-1">Name</label>
                            <input name="name" className="w-full p-2 border rounded" placeholder="Your full name" minLength={2} />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-700 mb-1">Email</label>
                            <input name="email" type="email" className="w-full p-2 border rounded" placeholder="you@example.com" />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-700 mb-1">Number</label>
                            <input name="phone" type="tel" className="w-full p-2 border rounded" placeholder="Your Phone Number" minLength={7} maxLength={15} />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-700 mb-1">Message</label>
                            <textarea name="message" rows="5" className="w-full p-2 border rounded" placeholder="How can we help?" minLength={5} />
                        </div>

                        <div className="flex items-center justify-between gap-3">
                            <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg" disabled={loading}>
                                {loading ? <CircularProgress size={24} color="inherit" /> : "Send Message"}
                            </button>
                            <button type="button" onClick={() => { setFormOpen(false); }} className="text-gray-600">Cancel</button>
                        </div>
                        <Snackbar
                            open={open}
                            autoHideDuration={6000}
                            onClose={handleClose}
                            message={updateMessage}
                            anchorOrigin={{
                                vertical: 'bottom', horizontal: 'center'
                            }}
                        />
                    </form>
                    <div className="mt-6 text-xs text-gray-500">
                        Or email us directly at <a href="mailto:support@fixerfind.com" className="text-blue-600">hello@fixerfind.com</a>
                    </div>
                </div>
            </div>

            {formOpen && <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={() => setFormOpen(false)} aria-hidden="true" />}
        </>
    );
}
